// ================================================================================================
import _ from "lodash";
import { PureComponent, ReactElement, CSSProperties, MouseEvent } from 'react';
import "./TreeStructureLite.less";
import { isDefined, IsNullOrWhiteSpace, isFunction, IsNullOrEmpty } from "@Utils/Micro";
import { FolderItemType, FolderStructure } from '@Primitive/TreeStructure';
import { getIconClassName } from '@Primitive/IconSelectorUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { ListItemProps } from '@Primitive/ListItem';
import { Props } from "@Utils/ReactProps";
// ================================================================================================
export interface TreeStructureLiteProps extends Props<TreeStructureLite> {
    FolderStructure: FolderStructure[];
    onListItemSelected?: (key: string, isFolder: boolean, ctrlKey: boolean) => void;
    style?: CSSProperties;
    onNestedListToggle?: (id: number, key: string) => void;
    withoutNesting?: boolean;

    /**
     * Limit the showing lines of secondary text.
     */
    secondaryTextLimitLine?: number;

    /**
     * Callback function fired when the toggle
     * item on the right side is clicked.
     */
    onRightToggleClick?: (id: number) => void;

    /**
     * Bubbles up the click event on folder items.
     */
    selectableFolders?: boolean;

    /**
      * Right buttons Supported in non-leafs?
     */
    hasRightButtonsInFolders?: boolean;
}
// ---------------------------------------------------------------------------------------------------
function stopPropagation(event: MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
}
// ---------------------------------------------------------------------------------------------------
export class TreeStructureLite extends PureComponent<TreeStructureLiteProps> {
    constructor(props: TreeStructureLiteProps) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return (
            <ul
                style={{ fontSize: 14, ...this.props.style }}
                className="tree-structure-lite-main-ul"
            >
                {this.getNestedList(this.props.FolderStructure, 0)}
            </ul>);
    }

    // ---------------------------------------------------------------------------------------------------
    private getNestedList(children: FolderStructure[], level: number): ReactElement<ListItemProps>[] {
        let items: ReactElement<ListItemProps>[] = [];

        const itemHeight = 40;
        const leftIconWidth = 20;
        const rightIconWidth = this.props.hasRightButtonsInFolders ? 70 : 40;
        const rightButtonWidth = this.props.hasRightButtonsInFolders ? 40 : 0;
        const nestedPadding = 15;

        const leftIconStyle: CSSProperties = {
            minWidth: leftIconWidth, height: itemHeight,
            display: "flex",
            marginRight: 10,
            marginLeft: 4,
            alignItems: "center"
        };
        const labelStyle: CSSProperties = {
            flex: 1,
            display: "inline-block",
            paddingRight: rightIconWidth,
        };
        const itemsStyle: CSSProperties = {
            minHeight: itemHeight, width: "100%",
            display: "flex", alignItems: "center"
        };
        const liStyle: CSSProperties = {
            paddingLeft: this.props.withoutNesting == true
                ? 0 : 5 + ((level > 0) ? nestedPadding : 0)
        };

        let secondaryStyle: CSSProperties = {
            fontSize: "90%", opacity: 0.7
        };

        if (isDefined(this.props.secondaryTextLimitLine)) {
            secondaryStyle.overflow = "hidden";
            secondaryStyle.textOverflow = "ellipsis";
            secondaryStyle.display = "-webkit-box";
            secondaryStyle.WebkitBoxOrient = "vertical";
            secondaryStyle.WebkitLineClamp = this.props.secondaryTextLimitLine;
            secondaryStyle.lineHeight = "13px";
            secondaryStyle.maxHeight = this.props.secondaryTextLimitLine * 13;
        }

        const handleItemClick = (
            e: MouseEvent,
            key: string,
            isFolder: boolean,
            ctrlKey: boolean
        ) => {
            // Target element is defined?
            if (!isDefined(e) || !isDefined(e.target))
                return;
            // Fire callback if element is clickable.
            const target: any = e.target;
            if (target.id.startsWith('clickable') || target == e.currentTarget) {
                if (isFunction(this.props.onListItemSelected))
                    this.props.onListItemSelected(key, isFolder, ctrlKey);
            }
        };

        children.forEach(element => {
            if (element.children.length > 0) {
                items.push(
                    <li
                        key={"dashboard-folder-" + element._id}
                        style={liStyle}
                    >
                        <div
                            key="title"
                            style={{ ...itemsStyle, ...element.style }}
                            onClick={e => {
                                this.handleNestedListToggle(element._id, element.key);
                                if (this.props.selectableFolders && element.selectable)
                                    handleItemClick(e, element.key, true, e.ctrlKey || e.metaKey);
                            }}>
                            {
                                (isDefined(element.leftFontIcon)) ?
                                    _.map(element.leftFontIcon, (i) => <i key={element._id} style={leftIconStyle} className={getIconClassName(i)} />) :
                                    <div
                                        key="left-icon"
                                        id={(this.props.selectableFolders ? '' : 'not--') + 'clickable--left--icon'}
                                        style={leftIconStyle}>
                                        {element.leftIcon}
                                    </div>
                            }
                            <div
                                key="text"
                                id={(this.props.selectableFolders ? '' : 'not--') + 'clickable--primary--text'}
                                style={labelStyle}>
                                {element.primaryText}
                            </div>
                            <div style={{ height: itemHeight, width: rightIconWidth }}
                                key="tree-structure-lite-icon-right"
                                className="tree-structure-lite-icon-right"
                            >
                                {/* Right Buttons */}
                                <div style={{ width: rightButtonWidth }} onClick={stopPropagation}>
                                    {element.rightIcon}
                                </div>

                                {/*Expand Arrows*/}
                                <div
                                    key="expand-arrow"
                                    className="expand-arrow"
                                    // Inform parent (if needs to) for the
                                    // click of toggle item on the right side.
                                    onClick={isFunction(this.props.onRightToggleClick) ? () => this.props.onRightToggleClick(element._id) : _.noop}
                                >
                                    <i className={"far " + ((element.open) ? "fa-angle-down" : "fa-angle-left")} />
                                </div>
                            </div>
                        </div>
                        {
                            (element.open) ?
                                <ul key="sub-ul" className="tree-structure-lite-sub-ul">
                                    {this.getNestedList(element.children, level + 1)}
                                </ul>
                                : null
                        }
                    </li>
                );
            } else if (element.type != FolderItemType.Folder) {
                let dynLabelStyle = labelStyle;
                if (_.isArray(element.leftIcon) &&
                    element.leftIcon.length > 0)
                    dynLabelStyle.paddingLeft = undefined;

                items.push(<li key={"dashboard-list-" + element.key} style={liStyle}>
                    <div
                        style={{ ...itemsStyle, ...element.style }}
                        onClick={e => handleItemClick(e, element.key, false, e.ctrlKey || e.metaKey)}>
                        {
                            (isDefined(element.leftFontIcon)) ?
                                _.map(element.leftFontIcon, (i) => <i key={`leftIcon-${i}`} id="clickable-leftIcon" style={leftIconStyle} className={getIconClassName(i)} />) :
                                <div key="clickable-leftIcon" id="clickable-leftIcon" style={leftIconStyle}>{element.leftIcon}</div>
                        }
                        {!IsNullOrEmpty(element.tooltip) ?
                            <Tippy
                                animation={false}
                                trigger={'mouseenter'}
                                popperOptions={{
                                    modifiers: [{
                                        name: 'preventOverflow',
                                        options: {
                                            escapeWithReference: true,
                                            boundariesElement: 'window'
                                        }
                                    }]
                                }}
                                theme="yodiwo"
                                content={element.tooltip}
                                placement="top-start"
                            >
                                <div id="clickable-text" style={dynLabelStyle}>
                                    <div id="clickable-primary" >
                                        {element.primaryText}
                                    </div>
                                    <div id="clickable-secondary" style={secondaryStyle}>
                                        {element.secondaryText}
                                    </div>
                                </div>
                            </Tippy> :
                            <div key="clickable-text" id="clickable-text" style={dynLabelStyle}>
                                <div id="clickable-primary" >
                                    {element.primaryText}
                                </div>
                                <div id="clickable-secondary" style={secondaryStyle}>
                                    {element.secondaryText}
                                </div>
                            </div>
                        }
                        <div key="tree-structure-lite-icon-right" className="tree-structure-lite-icon-right" style={{ height: itemHeight, width: rightIconWidth }}>
                            <div style={{ width: rightIconWidth - 8.75 }} >
                                {element.rightIcon}
                            </div>
                            {this.props.hasRightButtonsInFolders ? <div style={{ width: '8.75px' }} /> : null}
                        </div>
                    </div>
                    {
                        (isDefined(element.nestedItem)) ?
                            <div key="nested-items">
                                {element.nestedItem}
                            </div> :
                            null
                    }
                </li>);
            }
        });
        return items;
    }
    // ---------------------------------------------------------------------------------------------------
    handleNestedListToggle(id: number, key: string) {
        if (isFunction(this.props.onNestedListToggle))
            this.props.onNestedListToggle(id, key);
    }
    // ---------------------------------------------------------------------------------------------------
    public static helperPathSplitter<T>(
        items: T[],
        pathAccessor: string,
        itemToFolderStructure: (item: T, path: string, id: number) => FolderStructure,
        createFolderStructure: (filename: string, path: string, id: number) => FolderStructure
    ) {
        let folders: FolderStructure[] = [];
        let folderDictionary: { [path: string]: FolderStructure } = {};
        let id = 0;

        // Pass all items
        _.forEach(items, item => {
            let path = _.get(item, pathAccessor) as string;
            if (!IsNullOrWhiteSpace(path) && path != "/") {
                let folder = folderDictionary[path.toLocaleLowerCase()];
                if (!isDefined(folder)) {
                    // Create the folder path
                    let tempPath = "/";
                    let split = path.split("/");
                    for (var i = 0; i < split.length; i++) {
                        if (!IsNullOrWhiteSpace(split[i])) {
                            tempPath = (tempPath + split[i] + "/").toLocaleLowerCase();
                            let tmpFolder = folderDictionary[tempPath];
                            if (!isDefined(tmpFolder)) {
                                id++;
                                tmpFolder = createFolderStructure(split[i], tempPath, id);
                                if (isDefined(folder))
                                    folder.children.push(tmpFolder);
                                else
                                    folders.push(tmpFolder);
                                folderDictionary[tempPath] = tmpFolder;
                            }
                            folder = tmpFolder;
                        }
                    }
                }

                if (isDefined(folder))
                    folder.children.push(itemToFolderStructure(item, path, id));
                else
                    folders.push(itemToFolderStructure(item, "/", id));
            } else {
                folders.push(itemToFolderStructure(item, "/", id));
            }
            id++;
        });

        return folders;
    }
    // ---------------------------------------------------------------------------------------------------
}
